import { ReadonlySignal, Signal, computed, signal } from '@preact/signals'
import { Barcode, Symbology, SymbologyDescription, TrackedBarcode } from '@scandit/web-datacapture-barcode'
import {
  type CapturedLabel,
  LabelFieldType,
  type LabelDateResult,
  type LabelField,
  type ReceiptScanningLineItem,
  type ReceiptScanningResult,
} from '@scandit/web-datacapture-label'
import type { DateFormat } from './LabelCaptureSettingsStore'

type Id = string

export class StorableBarcode {
  data: string | null
  symbology: Symbology
  addOnData: string | null
  id: Id
  trackedIdentifier: string = ''
  symbologyDescription: SymbologyDescription
  quantity: Signal<number>
  fieldName: string | null = null

  constructor(barcode: Barcode) {
    const { data, symbology, addOnData } = barcode
    this.data = data
    this.symbology = symbology
    this.symbologyDescription = new SymbologyDescription(symbology)
    this.addOnData = addOnData
    this.id = StorableBarcode.idForBarcode(barcode)

    this.quantity = signal(1)
  }

  public static fromTrackedBarcode(trackedBarcode: TrackedBarcode) {
    const instance = new this(trackedBarcode.barcode)
    instance.trackedIdentifier = String(trackedBarcode.identifier)
    return instance
  }

  public static areEqual(storableBarcode1: StorableBarcode, storableBarcode2: StorableBarcode) {
    return storableBarcode1.id === storableBarcode2.id
  }

  public static idForBarcode({ data, symbology, addOnData }: Barcode): Id {
    return `${data}-${symbology}-${addOnData ?? ''}`
  }
}

interface CapturedLabelField {
  name: string
  value: string
}

const DATE_COMPONENT_ORDER: Record<DateFormat, ('day' | 'month' | 'year')[]> = {
  dmy: ['day', 'month', 'year'],
  ymd: ['year', 'month', 'day'],
  mdy: ['month', 'day', 'year'],
}

export function formatDate(date: LabelDateResult, dateFormat: DateFormat): string {
  return DATE_COMPONENT_ORDER[dateFormat]
    .map((component) => date[component])
    .filter((value) => value != null)
    .join(' - ')
}

function extractFieldInfo(field: LabelField, dateFormat: DateFormat): CapturedLabelField {
  let value: string

  switch (field.type) {
    case LabelFieldType.Text: {
      const date = field.date
      if (date) {
        value = formatDate(date, dateFormat)
      } else {
        value = field.text ?? 'N/A'
      }
      break
    }
    case LabelFieldType.Barcode: {
      value = field.barcode?.data ?? field.text ?? 'N/A'
      break
    }
    default: {
      value = field.date ? formatDate(field.date, dateFormat) : (field.barcode?.data ?? field.text ?? 'N/A')
    }
  }

  return { name: field.name, value }
}

/**
 * A non-barcode result row: VIN, validation-flow field, receipt header/total, etc.
 * Identified by an opaque `id` (callers compose a prefix to keep namespaces separate).
 */
export class StorableField {
  quantity: Signal<number> = signal(1)

  constructor(
    public fieldName: string,
    public data: string,
    public id: Id
  ) {}
}

export function formatReceiptValue(value: string | number | null | undefined): string {
  if (value == null || value === '') return 'N/A'
  return String(value)
}

export function formatReceiptLineItem(
  name: string,
  totalPrice: number | null,
  unitPrice: number | null,
  quantity: number,
  discount: number | null
): string {
  let result = `${name}: ${formatReceiptValue(totalPrice)}`
  if (unitPrice != null) {
    result += ` (${unitPrice} x ${quantity})`
  }
  if (discount != null) {
    result += ` - ${discount}`
  }
  return result
}

export function buildVinDisplayValue(barcodeData: string | null, text: string | null): string {
  if (barcodeData) return barcodeData
  if (text) return text
  return 'N/A'
}

function labelFieldToStorable(field: LabelField, dateFormat: DateFormat): StorableField {
  const { name, value } = extractFieldInfo(field, dateFormat)
  return new StorableField(name, value, `label-${name}-${value}`)
}

function receiptLineItemToStorable(item: ReceiptScanningLineItem): StorableField {
  const data = formatReceiptLineItem(item.name, item.totalPrice, item.unitPrice, item.quantity, item.discount)
  return new StorableField(item.name, data, `receipt-line-${data}`)
}

function receiptHeaderToStorable(fieldName: string, data: string): StorableField {
  return new StorableField(fieldName, data, `receipt-field-${fieldName}-${data}`)
}

function vinLabelToStorable(label: CapturedLabel): StorableField {
  const barcodeData = label.fields[0]?.barcode?.data ?? null
  const text = label.fields[1]?.text ?? null
  const data = buildVinDisplayValue(barcodeData, text)
  return new StorableField('VIN', data, `vin-${data}`)
}

export type StorableItem = StorableBarcode | StorableField

export class ResultItemsListStore {
  private map: Map<string, StorableItem> = new Map()
  public resultItems = signal<StorableItem[]>([])
  public totalQuantity: ReadonlySignal<number>
  public dateFormat: DateFormat = 'mdy'
  public isEmpty: ReadonlySignal<boolean>

  constructor() {
    this.totalQuantity = computed(() =>
      this.resultItems.value.reduce((acc, code) => {
        acc += code.quantity.value
        return acc
      }, 0)
    )
    this.isEmpty = computed(() => this.totalQuantity.value === 0)
  }

  public setDateFormat(dateFormat: DateFormat) {
    this.dateFormat = dateFormat
  }

  public add(items: StorableItem[]) {
    for (const item of items) {
      const alreadyPresent = this.map.get(item.id)
      if (alreadyPresent) {
        alreadyPresent.quantity.value += 1
      } else {
        this.map.set(item.id, item)
      }
    }
    // reverse cause we want last item on top
    this.resultItems.value = [...this.map.values()].reverse()
  }

  public addFromBarcodes(barcodes: Barcode[]) {
    this.add(barcodes.map((barcode) => new StorableBarcode(barcode)))
  }

  public addFromLabelFields(fields: LabelField[]) {
    this.add(fields.map((field) => labelFieldToStorable(field, this.dateFormat)))
  }

  public addFromReceiptScanningResult(result: ReceiptScanningResult) {
    const merchantAddress = [result.storeAddress, result.storeCity].filter(Boolean).join(', ')
    const items: StorableItem[] = [
      receiptHeaderToStorable('Merchant Name', formatReceiptValue(result.storeName)),
      receiptHeaderToStorable('Merchant Address', formatReceiptValue(merchantAddress)),
      receiptHeaderToStorable('Date', formatReceiptValue(result.date)),
      receiptHeaderToStorable('Time', formatReceiptValue(result.time)),
      receiptHeaderToStorable('Number of Items', String(result.lineItems.length)),
      ...result.lineItems.map(receiptLineItemToStorable),
      receiptHeaderToStorable('Subtotal', formatReceiptValue(result.paymentPreTaxTotal)),
      receiptHeaderToStorable('Tax', formatReceiptValue(result.paymentTax)),
      receiptHeaderToStorable('Total', formatReceiptValue(result.paymentTotal)),
    ]
    this.add(items)
  }

  public addFromCapturedVinLabel(label: CapturedLabel) {
    this.add([vinLabelToStorable(label)])
  }

  public addFromTrackedBarcodes(trackedBarcodes: TrackedBarcode[]) {
    this.add(trackedBarcodes.map((trackedBarcode) => StorableBarcode.fromTrackedBarcode(trackedBarcode)))
  }

  public removeAll() {
    this.map.clear()
    this.resultItems.value = [...this.map.values()]
  }
}
